import { ContractVisitGroup } from '../components/VisitGroupEditor'

export type VisitGroupScheduleMode = 'primary' | 'piggyback'

export interface VisitGroupServiceAssignment {
  service_id: string
  schedule_mode: VisitGroupScheduleMode
  recurrence_type?: string | null
  recurrence_days?: number[]
  recurrence_weekday?: number | null
  recurrence_occurrence?: number | null
  recurrence_day_of_month?: number | null
  /** Day-of-month piggyback that fires on the group's visit date nearest that day. */
  recurrence_nearest_visit?: boolean | null
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAY_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

/** Find the visit-group assignment for a contract service, if any. */
export function findServiceVisitAssignment(
  serviceId: string,
  visitGroups: ContractVisitGroup[]
): { group: ContractVisitGroup; assignment: VisitGroupServiceAssignment } | null {
  for (const group of visitGroups) {
    for (const assignment of getGroupAssignments(group)) {
      if (assignment.service_id === serviceId) {
        return { group, assignment }
      }
    }
  }
  return null
}

/** Human-readable schedule for a contract service (including piggyback cadence). */
export function describeContractServiceSchedule(
  service: {
    id: string
    recurrence_type?: string | null
    recurrence_days?: number[]
    schedule?: {
      recurrence_type?: string | null
      recurrence_days?: number[]
      recurrence_day_of_month?: number | null
      recurrence_weekday?: number | null
      recurrence_occurrence?: number | null
    }
  },
  visitGroups: ContractVisitGroup[] = []
): string | null {
  const linked = findServiceVisitAssignment(service.id, visitGroups)
  const assignment = linked?.assignment
  const rtype =
    assignment?.recurrence_type ||
    service.recurrence_type ||
    service.schedule?.recurrence_type ||
    null
  if (!rtype) return null

  if (rtype === 'daily') return 'Daily'

  if (rtype === 'bi_weekly' || rtype === 'biweekly' || rtype === 'bi-weekly') {
    const days = (assignment?.recurrence_days ||
      service.recurrence_days ||
      service.schedule?.recurrence_days ||
      []) as number[]
    const labels = days.map((d) => DAY_LABELS[d]).filter(Boolean)
    return labels.length > 0 ? `Every other week — ${labels.join(', ')}` : null
  }

  if (rtype === 'monthly') {
    const dom =
      assignment?.recurrence_day_of_month ??
      service.schedule?.recurrence_day_of_month ??
      null
    if (dom != null) {
      let text = `Monthly on day ${dom}`
      if (assignment?.recurrence_nearest_visit) {
        text += ' — runs on the weekly visit day closest to that date'
        if (linked?.group.name) text += ` (${linked.group.name})`
      }
      return text
    }
    const weekday =
      assignment?.recurrence_weekday ?? service.schedule?.recurrence_weekday ?? null
    const occurrence =
      assignment?.recurrence_occurrence ?? service.schedule?.recurrence_occurrence ?? null
    if (weekday != null && occurrence != null) {
      const ordinals = ['1st', '2nd', '3rd', '4th', '5th']
      const occ = ordinals[occurrence - 1] || `${occurrence}th`
      return `Monthly on the ${occ} ${WEEKDAY_FULL[weekday] || ''}`.trim()
    }
    return 'Monthly'
  }

  if (rtype === 'weekly') {
    const days = (service.recurrence_days || service.schedule?.recurrence_days || []) as number[]
    const labels = days.map((d) => DAY_LABELS[d]).filter(Boolean)
    if (labels.length > 0) return `Weekly — ${labels.join(', ')}`
    return null
  }

  return String(rtype).replace(/_/g, ' ')
}

/** Normalize legacy service_ids to assignments (all primary). */
export function getGroupAssignments(group: ContractVisitGroup): VisitGroupServiceAssignment[] {
  if (group.service_assignments && group.service_assignments.length > 0) {
    return group.service_assignments
  }
  return (group.service_ids || []).map((service_id) => ({
    service_id,
    schedule_mode: 'primary' as const,
  }))
}

export function assignmentsToServiceIds(assignments: VisitGroupServiceAssignment[]): string[] {
  return assignments.map((a) => a.service_id)
}

export function syncGroupServiceIds(group: ContractVisitGroup): ContractVisitGroup {
  // Trust an explicitly-provided service_assignments array (even when empty) so callers
  // can intentionally clear a group; only fall back to service_ids when assignments are
  // absent (legacy groups). Using getGroupAssignments here would resurrect a just-removed
  // service from the still-populated service_ids, re-adding it and defeating the sync.
  const assignments = group.service_assignments ?? getGroupAssignments(group)
  return {
    ...group,
    service_assignments: assignments,
    service_ids: assignmentsToServiceIds(assignments),
  }
}

/** Whether a piggyback assignment fires on a given date. */
export function piggybackMatchesDate(
  assignment: VisitGroupServiceAssignment,
  date: Date,
  anchorDate: Date
): boolean {
  if (assignment.schedule_mode !== 'piggyback') return true

  const rtype = assignment.recurrence_type || 'monthly'
  const rdays = assignment.recurrence_days || []
  const dow = date.getDay()

  if (rtype === 'weekly') {
    return rdays.length === 0 || rdays.includes(dow)
  }
  if (rtype === 'bi_weekly') {
    if (!rdays.includes(dow)) return false
    const diffDays = Math.floor((date.getTime() - anchorDate.getTime()) / (1000 * 60 * 60 * 24))
    return Math.floor(diffDays / 7) % 2 === 0
  }
  if (rtype === 'monthly') {
    if (assignment.recurrence_weekday != null && assignment.recurrence_occurrence != null) {
      return isNthWeekdayOfMonth(
        date,
        assignment.recurrence_weekday,
        assignment.recurrence_occurrence
      )
    }
    if (assignment.recurrence_day_of_month != null) {
      return date.getDate() === assignment.recurrence_day_of_month
    }
    return date.getDate() === anchorDate.getDate()
  }
  if (rtype === 'daily') return true
  return rdays.includes(dow)
}

/**
 * JS mirror of the SQL group-level `recurrence_matches_date` (7-arg overload).
 * Supports weekly, bi-weekly, daily, and monthly (nth-weekday / day-of-month, clamped).
 */
export function groupRecurrenceMatchesDate(
  recurrenceType: string,
  recurrenceDays: number[],
  date: Date,
  anchorDate: Date,
  monthly?: {
    weekday?: number | null
    occurrence?: number | null
    dayOfMonth?: number | null
  }
): boolean {
  const dow = date.getDay()
  if (recurrenceType === 'daily') return true
  if (recurrenceType === 'weekly') return recurrenceDays.includes(dow)
  if (recurrenceType === 'bi_weekly') {
    if (!recurrenceDays.includes(dow)) return false
    const diffDays = Math.floor((date.getTime() - anchorDate.getTime()) / (1000 * 60 * 60 * 24))
    return Math.floor(diffDays / 7) % 2 === 0
  }
  if (recurrenceType === 'monthly') {
    if (monthly?.weekday != null && monthly?.occurrence != null) {
      return isNthWeekdayOfMonth(date, monthly.weekday, monthly.occurrence)
    }
    if (monthly?.dayOfMonth != null) {
      const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
      return date.getDate() === Math.min(monthly.dayOfMonth, daysInMonth)
    }
    return date.getDate() === anchorDate.getDate()
  }
  if (recurrenceType === 'custom_interval') return recurrenceDays.includes(dow)
  return false
}

/**
 * Generalized "run on the group's visit day closest to that date" matcher.
 * Works for any piggyback cadence (monthly day-of-month, monthly nth-weekday,
 * weekly, bi-weekly): every date the piggyback's own schedule would ideally
 * fire on is snapped to the group's nearest visit date in that month, and the
 * service fires when `date` (a group visit date) is one of those snapped dates.
 */
export function piggybackNearestMatchesDate(
  assignment: VisitGroupServiceAssignment,
  date: Date,
  groupRecurrenceType: string,
  groupRecurrenceDays: number[],
  groupAnchor: Date,
  monthly?: { weekday?: number | null; occurrence?: number | null; dayOfMonth?: number | null }
): boolean {
  if ((assignment.recurrence_type || 'monthly') === 'daily') return true

  const year = date.getFullYear()
  const month = date.getMonth()
  const lastDay = new Date(year, month + 1, 0).getDate()

  for (let day = 1; day <= lastDay; day++) {
    const ideal = new Date(year, month, day)
    if (!piggybackMatchesDate(assignment, ideal, groupAnchor)) continue
    const snapped = nearestGroupVisitDate(
      ideal,
      groupRecurrenceType,
      groupRecurrenceDays,
      groupAnchor,
      day,
      monthly
    )
    if (snapped && snapped.getTime() === date.getTime()) return true
  }
  return false
}

/** The group visit date within date's month closest to targetDom (JS mirror of SQL). */
export function nearestGroupVisitDate(
  monthDate: Date,
  recurrenceType: string,
  recurrenceDays: number[],
  anchorDate: Date,
  targetDom: number | null | undefined,
  monthly?: { weekday?: number | null; occurrence?: number | null; dayOfMonth?: number | null }
): Date | null {
  const year = monthDate.getFullYear()
  const month = monthDate.getMonth()
  const lastDay = new Date(year, month + 1, 0).getDate()
  let best: Date | null = null
  let bestDiff = Number.POSITIVE_INFINITY
  for (let day = 1; day <= lastDay; day++) {
    const candidate = new Date(year, month, day)
    if (groupRecurrenceMatchesDate(recurrenceType, recurrenceDays, candidate, anchorDate, monthly)) {
      const diff = Math.abs(day - (targetDom ?? day))
      if (diff < bestDiff) {
        bestDiff = diff
        best = candidate
      }
    }
  }
  return best
}

export function isNthWeekdayOfMonth(date: Date, weekday: number, occurrence: number): boolean {
  if (date.getDay() !== weekday) return false
  const year = date.getFullYear()
  const month = date.getMonth()

  if (occurrence === 5) {
    const lastDay = new Date(year, month + 1, 0).getDate()
    for (let d = lastDay; d >= 1; d--) {
      const candidate = new Date(year, month, d)
      if (candidate.getDay() === weekday) {
        return date.getDate() === d
      }
    }
    return false
  }

  let count = 0
  for (let d = 1; d <= date.getDate(); d++) {
    const candidate = new Date(year, month, d)
    if (candidate.getDay() === weekday) {
      count++
      if (d === date.getDate()) {
        return count === occurrence
      }
    }
  }
  return false
}

export function formatPiggybackLabel(assignment: VisitGroupServiceAssignment): string {
  if (assignment.schedule_mode !== 'piggyback') return ''
  const rtype = assignment.recurrence_type || 'monthly'
  if (rtype === 'monthly' && assignment.recurrence_weekday != null) {
    const occLabels = ['1st', '2nd', '3rd', '4th', 'Last']
    const occ = assignment.recurrence_occurrence || 1
    const occLabel = occLabels[Math.min(occ, 5) - 1] || '1st'
    return `${occLabel} ${DAY_LABELS[assignment.recurrence_weekday]} monthly`
  }
  if (rtype === 'weekly' && assignment.recurrence_days?.length) {
    return assignment.recurrence_days.map((d) => DAY_LABELS[d]).join('/') + ' weekly'
  }
  return rtype
}

export function moveServiceToGroup(
  groups: ContractVisitGroup[],
  serviceId: string,
  targetGroupId: string,
  assignment?: Partial<VisitGroupServiceAssignment>
): ContractVisitGroup[] {
  const cleaned = groups.map((g) => {
    const assignments = getGroupAssignments(g).filter((a) => a.service_id !== serviceId)
    return syncGroupServiceIds({ ...g, service_assignments: assignments })
  })

  return cleaned.map((g) => {
    if (g.id !== targetGroupId) return g
    const existing = getGroupAssignments(g)
    const next: VisitGroupServiceAssignment = {
      service_id: serviceId,
      schedule_mode: assignment?.schedule_mode || 'primary',
      recurrence_type: assignment?.recurrence_type ?? null,
      recurrence_days: assignment?.recurrence_days,
      recurrence_weekday: assignment?.recurrence_weekday ?? null,
      recurrence_occurrence: assignment?.recurrence_occurrence ?? null,
      recurrence_day_of_month: assignment?.recurrence_day_of_month ?? null,
      recurrence_nearest_visit: assignment?.recurrence_nearest_visit ?? null,
    }
    return syncGroupServiceIds({
      ...g,
      service_assignments: [...existing, next],
    })
  })
}

export function removeServiceFromGroups(
  groups: ContractVisitGroup[],
  serviceId: string
): ContractVisitGroup[] {
  return groups
    .map((g) => {
      const assignments = getGroupAssignments(g).filter((a) => a.service_id !== serviceId)
      return syncGroupServiceIds({ ...g, service_assignments: assignments })
    })
    .filter((g) => getGroupAssignments(g).length > 0)
}

/** Days shown for a visit group: own recurrence_days, else union of linked service days. */
export function resolveVisitGroupDisplayDays(
  group: ContractVisitGroup,
  services?: Array<{
    id: string
    recurrence_days?: number[]
    schedule?: { recurrence_days?: number[] }
  }>
): number[] {
  const own = (group.recurrence_days || [])
    .map(Number)
    .filter((d) => Number.isFinite(d))
  if (own.length > 0) return Array.from(new Set(own)).sort((a, b) => a - b)
  if (!services?.length) return []

  const linkedIds = new Set(getGroupAssignments(group).map((a) => a.service_id))
  const days = new Set<number>()
  for (const s of services) {
    if (!linkedIds.has(s.id)) continue
    const fromService = s.recurrence_days || s.schedule?.recurrence_days || []
    for (const d of fromService) {
      const n = Number(d)
      if (Number.isFinite(n)) days.add(n)
    }
  }
  return Array.from(days).sort((a, b) => a - b)
}

/** Copy service days onto weekly visit groups that were saved with an empty day list. */
export function fillEmptyVisitGroupDaysFromServices(
  groups: ContractVisitGroup[],
  services: Array<{
    id: string
    recurrence_days?: number[]
    schedule?: { recurrence_days?: number[] }
  }>
): ContractVisitGroup[] {
  if (!groups.length || !services.length) return groups
  return groups.map((group) => {
    if ((group.recurrence_days || []).length > 0) return group
    const rtype = group.recurrence_type || 'weekly'
    if (rtype !== 'weekly' && rtype !== 'bi_weekly' && rtype !== 'biweekly') return group
    const days = resolveVisitGroupDisplayDays(group, services)
    if (days.length === 0) return group
    return { ...group, recurrence_days: days }
  })
}

export function createGroupWithService(
  groups: ContractVisitGroup[],
  serviceId: string,
  contractId: string,
  contractStartDate?: string | null,
  contractEndDate?: string | null,
  serviceName?: string,
  assignment?: Partial<VisitGroupServiceAssignment>
): ContractVisitGroup[] {
  const tempId = `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`
  const days = Array.isArray(assignment?.recurrence_days) ? assignment!.recurrence_days! : []
  const recurrenceType = assignment?.recurrence_type || 'weekly'
  const newGroup: ContractVisitGroup = syncGroupServiceIds({
    id: tempId,
    contract_id: contractId,
    name: serviceName ? `${serviceName} Visit` : 'New Visit Group',
    recurrence_type: recurrenceType,
    recurrence_days: days,
    start_date: contractStartDate || null,
    end_date: contractEndDate || null,
    scheduled_start_time: null,
    scheduled_end_time: null,
    billing_driver: null,
    dsny_integration: false,
    dsny_pickup_days: [],
    is_active: true,
    service_ids: [],
    checklist_items: [],
    checklist_overrides: {},
    service_assignments: [
      {
        service_id: serviceId,
        schedule_mode: assignment?.schedule_mode || 'primary',
        recurrence_type: assignment?.recurrence_type ?? null,
        recurrence_days: assignment?.recurrence_days,
        recurrence_weekday: assignment?.recurrence_weekday ?? null,
        recurrence_occurrence: assignment?.recurrence_occurrence ?? null,
        recurrence_day_of_month: assignment?.recurrence_day_of_month ?? null,
        recurrence_nearest_visit: assignment?.recurrence_nearest_visit ?? null,
      },
    ],
  })
  return [...groups, newGroup]
}

export function mergeGroups(
  groups: ContractVisitGroup[],
  sourceId: string,
  targetId: string
): ContractVisitGroup[] {
  const source = groups.find((g) => g.id === sourceId)
  const target = groups.find((g) => g.id === targetId)
  if (!source || !target) return groups

  const mergedAssignments = [...getGroupAssignments(target)]
  for (const a of getGroupAssignments(source)) {
    if (!mergedAssignments.some((m) => m.service_id === a.service_id)) {
      mergedAssignments.push(a)
    }
  }

  const unionDays = Array.from(
    new Set([...(target.recurrence_days || []), ...(source.recurrence_days || [])])
  ).sort((a, b) => a - b)

  return groups
    .filter((g) => g.id !== sourceId)
    .map((g) =>
      g.id === targetId
        ? syncGroupServiceIds({
            ...g,
            recurrence_days: unionDays,
            dsny_integration: g.dsny_integration || source.dsny_integration,
            service_assignments: mergedAssignments,
          })
        : g
    )
}

export function getUnassignedServiceIds(
  groups: ContractVisitGroup[],
  allServiceIds: string[]
): string[] {
  const assigned = new Set(groups.flatMap((g) => getGroupAssignments(g).map((a) => a.service_id)))
  return allServiceIds.filter((id) => !assigned.has(id))
}

export function mappingRowFromAssignment(
  visitGroupId: string,
  contractServiceId: string,
  assignment: VisitGroupServiceAssignment
) {
  return {
    visit_group_id: visitGroupId,
    contract_service_id: contractServiceId,
    schedule_mode: assignment.schedule_mode || 'primary',
    recurrence_type: assignment.recurrence_type || null,
    recurrence_days: assignment.recurrence_days || null,
    recurrence_weekday: assignment.recurrence_weekday ?? null,
    recurrence_occurrence: assignment.recurrence_occurrence ?? null,
    recurrence_day_of_month: assignment.recurrence_day_of_month ?? null,
    recurrence_nearest_visit: assignment.recurrence_nearest_visit ?? null,
  }
}

export function normalizeVisitGroupFromDb(
  g: {
    id: string
    contract_id?: string
    name: string
    recurrence_type?: string
    recurrence_days?: number[]
    recurrence_weekday?: number | null
    recurrence_occurrence?: number | null
    recurrence_day_of_month?: number | null
    start_date?: string | null
    end_date?: string | null
    scheduled_start_time?: string | null
    scheduled_end_time?: string | null
    billing_driver?: string | null
    dsny_integration?: boolean
    dsny_pickup_days?: string[]
    is_active?: boolean
    checklist_items?: Array<{ label: string; sort_order: number; photo_required?: boolean }> | null
    checklist_overrides?: Record<
      string,
      Array<{ label: string; sort_order: number; photo_required?: boolean }>
    > | null
    assigned_crew_id?: string | null
    assigned_employee_id?: string | null
  },
  mappings: Array<{
    contract_service_id: string
    schedule_mode?: string
    recurrence_type?: string | null
    recurrence_days?: number[] | null
    recurrence_weekday?: number | null
    recurrence_occurrence?: number | null
    recurrence_day_of_month?: number | null
    recurrence_nearest_visit?: boolean | null
  }>,
  contractId: string
): ContractVisitGroup {
  const service_assignments: VisitGroupServiceAssignment[] = mappings.map((m) => ({
    service_id: m.contract_service_id,
    schedule_mode: (m.schedule_mode as VisitGroupScheduleMode) || 'primary',
    recurrence_type: m.recurrence_type ?? null,
    recurrence_days: m.recurrence_days || undefined,
    recurrence_weekday: m.recurrence_weekday ?? null,
    recurrence_occurrence: m.recurrence_occurrence ?? null,
    recurrence_day_of_month: m.recurrence_day_of_month ?? null,
    recurrence_nearest_visit: m.recurrence_nearest_visit ?? null,
  }))

  const overridesRaw = g.checklist_overrides
  const checklist_overrides: ContractVisitGroup['checklist_overrides'] = {}
  if (overridesRaw && typeof overridesRaw === 'object' && !Array.isArray(overridesRaw)) {
    for (const [key, val] of Object.entries(overridesRaw)) {
      if (Array.isArray(val)) {
        checklist_overrides[key] = val.map((item, index) => ({
          label: String((item as any)?.label || ''),
          sort_order:
            typeof (item as any)?.sort_order === 'number' ? (item as any).sort_order : index,
          photo_required: !!(item as any)?.photo_required,
        }))
      }
    }
  }

  return syncGroupServiceIds({
    id: g.id,
    contract_id: g.contract_id || contractId,
    name: g.name || '',
    recurrence_type: g.recurrence_type || 'weekly',
    recurrence_days: g.recurrence_days || [],
    recurrence_weekday: g.recurrence_weekday ?? null,
    recurrence_occurrence: g.recurrence_occurrence ?? null,
    recurrence_day_of_month: g.recurrence_day_of_month ?? null,
    start_date: g.start_date || null,
    end_date: g.end_date || null,
    scheduled_start_time: g.scheduled_start_time || null,
    scheduled_end_time: g.scheduled_end_time || null,
    billing_driver: g.billing_driver || null,
    dsny_integration: g.dsny_integration || false,
    dsny_pickup_days: g.dsny_pickup_days || [],
    is_active: g.is_active !== false,
    checklist_items: Array.isArray(g.checklist_items) ? g.checklist_items : [],
    checklist_overrides,
    assigned_crew_id: g.assigned_crew_id || null,
    assigned_employee_id: g.assigned_employee_id || null,
    service_ids: [],
    service_assignments,
  })
}

type ContractServiceForRepair = {
  id: string
  service_name?: string
  name?: string
  recurrence_days?: number[]
  dsny_integration?: boolean
}

/**
 * Attach real contract_services to visit groups that were saved with days/names but
 * no service mappings (DSNY package create uses temp dsny_* ids on groups).
 */
export function repairVisitGroupsMissingServices(
  groups: ContractVisitGroup[],
  contractServices: ContractServiceForRepair[]
): ContractVisitGroup[] {
  if (groups.length === 0 || contractServices.length === 0) return groups

  return groups.map((group) => {
    const assignments = getGroupAssignments(group)
    const hasRealAssignment = assignments.some(
      (a) => a.service_id && contractServices.some((s) => s.id === a.service_id)
    )
    if (hasRealAssignment) return group

    const groupDays = new Set(group.recurrence_days || [])
    const match =
      contractServices.find((s) =>
        (s.recurrence_days || []).some((d) => groupDays.has(d))
      ) ||
      contractServices.find(
        (s) =>
          (s.service_name || s.name || '').toLowerCase().includes('building upkeep') ||
          s.dsny_integration
      ) ||
      contractServices[0]
    if (!match) return group

    return syncGroupServiceIds({
      ...group,
      service_ids: [match.id],
      service_assignments: [{ service_id: match.id, schedule_mode: 'primary' }],
    })
  })
}
